import React from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';

import { Profile } from '@paragon/contract-library';
import { DistanceField, NumericField } from '@paragon/material';

import { updateInsertingProfileTrussSettings } from '../../../../../../store/actions/profileActions';
import { isInsertingANonStructuralGable } from '../../../../../../store/selectors/profileSelectors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { ActiveComponentContinuouslyBearingSettings } from '../../../properties/ContinuouslyBearingSettings';
import { LabeledBox } from '../../../properties/LabeledBox';
import SettingsButtons from '../../../properties/SettingsButtons';
import { BearingList } from './BearingList';

export function ProfileTrussStructuralDetails() {
  const webInteractionsProhibited = useAppSelector(isInsertingANonStructuralGable);

  return (
    <>
      <GeneralInfo />

      <LabeledBox title="Bearings">
        <BearingList />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ActiveComponentContinuouslyBearingSettings />
        </Box>
      </LabeledBox>

      {!webInteractionsProhibited && (
        <Alert severity="info" variant="outlined" sx={{ marginX: 1, marginY: 2, textWrap: 'wrap' }}>
          <AlertTitle>Did you know?</AlertTitle>
          In the scene on the left, you can:
          <ul>
            <li>add or remove panels by right-clicking on profile lines</li>
            <li>delete web segments by right-clicking or selecting and using the delete key</li>
            <li>add new web segments by clicking on two web joints</li>
          </ul>
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-evenly', mt: 2 }}>
        <SettingsButtons />
      </Box>
    </>
  );
}

const textFieldSx = { m: 1, flex: 1 } as const;

function GeneralInfo() {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);

  if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component');

  const settings = insertingObjectInfo.designSettings;
  if (settings.definitionType !== 'Profile') throw new Error('Expected to be inserting a profile truss');

  const plies = settings.profile.plies;
  const componentThickness = 1.5;
  const spacingMinimum = plies * componentThickness;

  const onChangeProfileProperty =
    <K extends keyof Profile>(key: K) =>
    (value: Profile[K]) => {
      if (settings.profile[key] !== value) {
        dispatch(
          updateInsertingProfileTrussSettings({
            ...settings,
            profile: { ...settings.profile, [key]: value },
          }),
        );
      }
    };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <DistanceField
          label="Spacing"
          value={settings.profile.spacing}
          onSubmit={onChangeProfileProperty('spacing')}
          sx={textFieldSx}
          min={spacingMinimum}
        />
        <NumericField
          label="Plies"
          sx={textFieldSx}
          value={plies}
          min={1}
          onSubmit={onChangeProfileProperty('plies')}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <DistanceField
          label="Max Top Panel Length"
          value={settings.profile.topChordMaxPanelLength}
          onSubmit={onChangeProfileProperty('topChordMaxPanelLength')}
          sx={{ mx: 1 }}
        />
        <DistanceField
          label="Max Bottom Panel Length"
          value={settings.profile.bottomChordMaxPanelLength}
          onSubmit={onChangeProfileProperty('bottomChordMaxPanelLength')}
          sx={{ mx: 1 }}
        />
      </Box>
    </>
  );
}
